import styled from "styled-components/native";
import OptionHeader from "../OptionHeader";
import { FlatList } from "react-native";
import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import SquareIconView from "../SquareIconView";
import { getProductColor } from "@scenes/pdp/utils/utils";
import { getProductLevelColor } from "@components/molecules/MatchStoneGalleryModel/Utils";
import { useTheme } from "styled-components/native";
import { SquareIconButtonProps } from "./types";
import { useEffect, useRef, useState } from "react";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { ButtonStyle } from "@scenes/configurator/JewelryConfigurator/types";
import { SCROLL_STEP } from "@utils/constants";
import { isKeyPresent } from "@scenes/configurator/JewelryConfigurator/utils";
import JewelryBuilderErrorMessage from "../JewelryBuilderErrorMessage";
import ResetButtonComponent from "../ResetButton";
import React from "react";
import { DefaultTheme } from "styled-components/native";

const SquareIconButton = ({
  productType,
  configObject,
  maxWidth,
  buttonSize = ButtonStyle.MEDIUM_02,
  showCarousel = false,
  fromReviewPage,
  isOpen,
  onToggle,
  fromOrderCart,
  fromRequest,
}: SquareIconButtonProps) => {
  const theme = useTheme();
  const productColor = getProductLevelColor(productType);
  const borderColor = getProductColor(productType);
  const {
    updateJewelryConfigInfo,
    jcSelectedOption,
    addToCartPressed,
    fetchValidationMessage,
    validateRequiredOptions,
  } = useJewelryConfiguratorState();

  const [selectedItem, setSelectedItem] = useState(
    configObject?.selected_item ??
      jcSelectedOption.current.option_value[configObject.option_type]
  );

  useEffect(() => {}, [addToCartPressed]);

  const horizontalPadding = SpaceType.Double * 2;
  const gapBetweenItems = SpaceType.Basic;
  const availableWidth = maxWidth - horizontalPadding;
  const column = Math.floor(
    availableWidth /
      ((buttonSize === ButtonStyle.SMALL ? 48 : 75) + gapBetweenItems)
  );

  const flatListRef = useRef(null);
  const isLastItemVisible = useRef(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [maxOffset, setMaxOffset] = useState(0);

  useEffect(() => {
    setSelectedItem(
      configObject?.selected_item
        ? configObject?.selected_item
        : jcSelectedOption.current.option_value[configObject.option_type]
    );
  }, [
    configObject?.selected_item,
    jcSelectedOption.current.option_value[configObject.option_type],
  ]);

  useEffect(() => {
    if (jcSelectedOption.current.option_value[configObject.option_type]) {
      setSelectedItem(
        jcSelectedOption.current.option_value[configObject.option_type]
      );
    }
  }, [jcSelectedOption, addToCartPressed]);

  const onViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: any[] }) => {
      const lastIndex = configObject.option_value.length - 1;
      isLastItemVisible.current = viewableItems.some(
        (item) => item.index === lastIndex
      );
    }
  ).current;

  const scrollList = (direction: string) => {
    let newOffset =
      direction === "left"
        ? Math.max(0, currentOffset - SCROLL_STEP)
        : Math.min(maxOffset, currentOffset + SCROLL_STEP);
    setCurrentOffset(newOffset);
    (flatListRef.current as any)?.scrollToOffset({
      offset: newOffset,
      animated: true,
    });
  };

  const handleScroll = (event: any) => {
    const offsetX = event.nativeEvent.contentOffset.x;
    setCurrentOffset(offsetX);
  };

  const handleContentSizeChange = (contentWidth: number) => {
    setMaxOffset(contentWidth - SCROLL_STEP);
  };

  return (
    <ContainerView>
      <OptionHeader
        title={configObject.option_name}
        value={
          configObject.option_value.find((item) => item.value === selectedItem)
            ?.label
        }
        productType={productType}
        showCarousel={showCarousel}
        helperText={configObject.option_helper_text}
        onLeftSliderPress={() => {
          scrollList("left");
        }}
        onRightSliderPress={() => {
          if (!isLastItemVisible.current) {
            scrollList("right");
          }
        }}
        fromReviewPage={fromReviewPage}
        isOpen={isOpen}
        onToggle={onToggle}
        fromOrderCart={fromOrderCart}
        fromRequest={fromRequest}
        disableToggleButton={configObject?.disable_modify_variation ?? false}
      />
      {(isOpen || !fromReviewPage) && (
        <>
          <Spacer size={SpaceType.Extra} />
          <FlatList
            ref={flatListRef}
            data={configObject.option_value}
            key={`${configObject.option_type}-${
              showCarousel ? "carousel" : column
            }`}
            {...(!showCarousel && { numColumns: column })}
            {...(!showCarousel &&
              column > 1 && { columnWrapperStyle: { gap: SpaceType.Basic } })}
            contentContainerStyle={{ gap: SpaceType.Basic }}
            horizontal={showCarousel}
            onScroll={handleScroll}
            onContentSizeChange={(width) => handleContentSizeChange(width)}
            showsHorizontalScrollIndicator={false}
            {...(showCarousel && {
              onViewableItemsChanged,
              viewabilityConfig: {
                itemVisiblePercentThreshold: 100,
              },
            })}
            renderItem={({ item }) => {
              return (
                <SquareIconView
                  backgroundColor={
                    selectedItem === item?.value
                      ? theme[productColor]
                      : theme["option-bg-static"]
                  }
                  borderColor={
                    selectedItem === item?.value
                      ? theme[borderColor as keyof DefaultTheme]
                      : theme["option-bg-static"]
                  }
                  isSelected={selectedItem === item?.value}
                  item={item}
                  buttonSize={buttonSize}
                  onPress={(selected) => {
                    if (item?.enabled) {
                      setSelectedItem(selected.value);
                    }
                    updateJewelryConfigInfo(configObject?.option_type, [
                      configObject,
                      selected.slug || selected.value,
                    ]);
                    if (
                      isKeyPresent(
                        configObject.option_type,
                        jcSelectedOption.current.option_error
                      )
                    ) {
                      validateRequiredOptions();
                    }
                  }}
                />
              );
            }}
          />
          <BottomContainer
            showError={isKeyPresent(
              configObject.option_type,
              jcSelectedOption.current.option_error
            )}
          >
            {isKeyPresent(
              configObject.option_type,
              jcSelectedOption.current.option_error
            ) && (
              <JewelryBuilderErrorMessage
                message={fetchValidationMessage(
                  configObject.option_name,
                  false
                )}
              />
            )}
            {configObject?.reset_button_visible && (
              <ResetButtonComponent productType={productType} />
            )}
          </BottomContainer>
        </>
      )}
    </ContainerView>
  );
};

const ContainerView = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  padding: 12px 16px;
`;

const BottomContainer = styled.View<{
  showError: boolean;
}>`
  flex-direction: row;
  justify-content: ${({ showError }: { showError: boolean }) =>
    showError ? "space-between" : "flex-end"};
  margin-top: 8px;
`;

export default SquareIconButton;
